import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DoneSection } from '../components/work/DoneSection';
import { ChevronDown, Eye, Mail } from 'lucide-react';
import { PageFrame } from '../components/work/page-chrome';
import { ErrorAlert, LoadingRows } from '../components/work/page-states';
import { PriorityLabel, WorkItemKeyLink, WorkItemTitleLink } from '../components/work/table-cells';
import { useOverlays } from '../components/work/overlay-context';
import { UserAvatar } from '../components/UserAvatar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { apiFetch, v1Path } from '../lib/api';
import { startViewAs } from '../lib/view-as';
import { useSession } from '../components/session-context';
import { forecastCapacity, nextFreeCopy } from '../lib/capacity-forecast';
import { formatThisWeekRange } from '../lib/dates';
import {
  loadBandLabel,
  resourceDisplayName,
  type ResourceRow,
  type ResourceWorkItem,
} from '../lib/resource-types';
import { cn } from '../lib/utils';

type Tab = 'overview' | 'work' | 'projects';

const TAB_LABELS: Record<Tab, string> = {
  overview: 'Overview',
  work: 'Work',
  projects: 'Streams',
};

const EXCEPTION = 'text-destructive';

export default function ResourceDetail() {
  const { userId } = useParams<{ userId: string }>();
  const { openCreateWorkItem, toast } = useOverlays();
  const session = useSession();
  const canViewAs = session.isAdmin && !session.viewAs && Boolean(userId) && userId !== session.userId;
  const [row, setRow] = useState<ResourceRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');

  const load = useCallback(async () => {
    if (!userId) {
      setError('Missing person.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setRow(await apiFetch<ResourceRow>(v1Path(`/resources/${userId}`)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load team member');
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const grouped = useMemo(() => {
    if (!row) {
      return { now: [], next: [], blocked: [], unplanned: [], started: 0, queued: 0 };
    }
    const now = row.items.filter(
      (item) => item.state_group === 'started' && item.type !== 'incident' && !item.blocked,
    );
    const next = row.items.filter(
      (item) =>
        item.type !== 'incident' &&
        !item.blocked &&
        (item.state_group === 'unstarted' || item.state_group === 'backlog'),
    );
    const blocked = row.items.filter((item) => item.blocked);
    return {
      now,
      next,
      blocked,
      unplanned: row.items.filter((item) => item.type === 'incident'),
      started: now.length,
      queued: next.length,
    };
  }, [row]);

  const projects = useMemo(() => {
    if (!row) return [];
    const byName = new Map<string, { name: string; hours: number; items: ResourceWorkItem[] }>();
    for (const item of row.items) {
      const current = byName.get(item.project_name) ?? {
        name: item.project_name,
        hours: 0,
        items: [],
      };
      current.hours += item.allocated_hours;
      current.items.push(item);
      byName.set(item.project_name, current);
    }
    return Array.from(byName.values()).sort((a, b) => b.hours - a.hours);
  }, [row]);

  const assignProjectId = row?.current?.project_id ?? row?.items[0]?.project_id;
  const engineering = row?.buckets.find((bucket) => bucket.key === 'engineering')?.hours ?? 0;
  const incidents = row?.buckets.find((bucket) => bucket.key === 'incidents')?.hours ?? 0;
  const overtime = row ? Math.round((row.allocated_hours - row.weekly_capacity_hours) * 10) / 10 : 0;

  return (
    <PageFrame>
      <div className="-mx-4 -mt-4 min-h-full bg-muted/40 px-4 pb-8 pt-4">
        <div className="space-y-5">
          <nav className="text-xs text-muted-foreground">
            <Link to="/resources" className="hover:text-foreground">
              Team
            </Link>
            {row ? (
              <>
                <span className="mx-1.5">/</span>
                <span className="text-foreground">{resourceDisplayName(row)}</span>
              </>
            ) : null}
          </nav>
          {error ? <ErrorAlert>{error}</ErrorAlert> : null}
          {loading ? <LoadingRows columns={4} /> : null}
          {!loading && row ? (
            <>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <UserAvatar name={resourceDisplayName(row)} src={row.avatar_url} size={64} />
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h1 className="text-[22px] font-semibold leading-7 tracking-tight">
                        {resourceDisplayName(row)}
                      </h1>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                          row.load_band === 'overloaded'
                            ? 'bg-destructive/10 text-destructive'
                            : 'bg-muted text-muted-foreground',
                        )}
                      >
                        {loadBandLabel(row.load_band)}
                      </span>
                    </div>
                    <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Mail className="h-3.5 w-3.5" aria-hidden />
                      {row.email}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {canViewAs ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        startViewAs(userId!).catch((err: unknown) =>
                          toast(err instanceof Error ? err.message : 'Could not switch to their view.'),
                        );
                      }}
                    >
                      <Eye />
                      See what {row.first_name || resourceDisplayName(row)} sees
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    disabled={!assignProjectId}
                    onClick={() => {
                      if (!assignProjectId) return;
                      openCreateWorkItem({ projectId: assignProjectId });
                    }}
                  >
                    Assign work
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap items-end justify-between gap-8">
                <div className="min-w-0 max-w-2xl flex-1 space-y-2">
                  <p className="flex flex-wrap items-baseline gap-2">
                    <span className="text-[28px] font-semibold leading-none tracking-tight tabular-nums">
                      {row.load_percent}%
                    </span>
                    <span className="text-sm text-muted-foreground">load</span>
                  </p>
                  <p className="text-sm tabular-nums text-muted-foreground">
                    {row.allocated_hours}h allocated / {row.weekly_capacity_hours}h this week
                    {' · '}
                    {formatThisWeekRange(new Date())}
                    {overtime > 0 ? (
                      <span className={cn('ml-1.5', EXCEPTION)}>· {overtime}h over</span>
                    ) : (
                      <span className="ml-1.5">· {row.available_hours}h remaining</span>
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {row.weekly_capacity_hours > 0
                      ? nextFreeCopy(
                          forecastCapacity(row.allocated_hours, row.weekly_capacity_hours, new Date()),
                        )
                      : 'No weekly capacity set'}
                  </p>
                  <CapacityTrack allocated={row.allocated_hours} capacity={row.weekly_capacity_hours} />
                </div>
                <dl className="grid grid-cols-4 gap-6">
                  <Count label="open" value={row.active_count} />
                  <Count label="active" value={grouped.started} />
                  <Count label="blocked" value={grouped.blocked.length} />
                  <Count label="queued" value={grouped.queued} />
                </dl>
              </div>

              <div className="flex gap-1 border-b border-border">
                {(['overview', 'work', 'projects'] as const).map((id) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setTab(id)}
                    className={cn(
                      '-mb-px border-b-2 px-3 py-2 text-sm',
                      tab === id
                        ? 'border-foreground font-medium text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {TAB_LABELS[id]}
                  </button>
                ))}
              </div>

              {tab === 'overview' ? (
                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="space-y-4">
                    <Panel
                      title="Current focus"
                      action={
                        <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setTab('work')}>
                          View all
                        </button>
                      }
                    >
                      <FocusBody item={row.current} />
                    </Panel>
                    <Panel title="Next">
                      <FocusBody item={row.next} />
                    </Panel>
                    <Panel
                      title="Workload"
                      action={
                        <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setTab('work')}>
                          View details
                        </button>
                      }
                    >
                      <ul className="space-y-3">
                        <HoursRow
                          label="Engineering"
                          hours={engineering}
                          capacity={row.weekly_capacity_hours}
                          hot={engineering > row.weekly_capacity_hours}
                        />
                        <HoursRow label="Incidents" hours={incidents} capacity={row.weekly_capacity_hours} />
                        <HoursRow label="Available" hours={row.available_hours} capacity={row.weekly_capacity_hours} muted />
                      </ul>
                    </Panel>
                  </div>
                  <Panel
                    title="Open work"
                    action={
                      <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setTab('work')}>
                        View all
                      </button>
                    }
                  >
                    {row.items.length === 0 ? (
                      <p className="text-sm text-muted-foreground">None</p>
                    ) : (
                      <ul className="divide-y">
                        {row.items.slice(0, 8).map((item) => (
                          <li key={item.id} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                            <WorkItemKeyLink
                              workItemId={item.id}
                              workItemKey={item.key}
                              projectId={item.project_id}
                            />
                            <span className="min-w-0 flex-1 truncate text-sm">{item.title}</span>
                            <PriorityLabel priority={item.priority} />
                            <span className="w-8 text-right text-xs tabular-nums text-muted-foreground">
                              {item.allocated_hours > 0 ? `${item.allocated_hours}h` : '—'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Panel>
                </div>
              ) : null}

              {tab === 'overview' && userId ? (
                <DoneSection scope={{ personId: userId }} showPeople={false} collapsible empty="Nothing marked as done yet." />
              ) : null}

              {tab === 'work' ? (
                <WorkTable>
                  <WorkSection title="Now" items={grouped.now} />
                  <WorkSection title="Blocked" items={grouped.blocked} />
                  <WorkSection title="Next" items={grouped.next} />
                  <WorkSection
                    title="Unplanned"
                    items={grouped.unplanned}
                    empty="No unplanned work this week."
                  />
                </WorkTable>
              ) : null}

              {tab === 'projects' ? (
                projects.length === 0 ? (
                  <Card className="rounded-xl border-border/80 shadow-none">
                    <CardContent className="p-5 text-sm text-muted-foreground">
                      No open work on any stream.
                    </CardContent>
                  </Card>
                ) : (
                  <WorkTable>
                    {projects.map((project) => (
                      <WorkSection key={project.name} title={project.name} items={project.items} />
                    ))}
                  </WorkTable>
                )
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </PageFrame>
  );
}

function Panel({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="rounded-xl border-border/80 shadow-none">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-sm font-medium">{title}</h2>
          {action}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function FocusBody({ item }: { item: ResourceWorkItem | null }) {
  if (!item) {
    return <p className="text-sm text-muted-foreground">None</p>;
  }
  return (
    <div className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <WorkItemKeyLink workItemId={item.id} workItemKey={item.key} projectId={item.project_id} />
        <WorkItemTitleLink
          workItemId={item.id}
          workItemKey={item.key}
          projectId={item.project_id}
          title={item.title}
        />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <PriorityLabel priority={item.priority} />
        <MetaChip>{item.state_name}</MetaChip>
        <MetaChip>{item.project_name}</MetaChip>
        {item.allocated_hours > 0 ? <MetaChip>{item.allocated_hours}h est.</MetaChip> : null}
      </div>
    </div>
  );
}

function MetaChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
      {children}
    </span>
  );
}

function HoursRow({
  label,
  hours,
  capacity,
  muted,
  hot,
}: {
  label: string;
  hours: number;
  capacity: number;
  muted?: boolean;
  hot?: boolean;
}) {
  const width = capacity > 0 ? Math.min(100, (hours / capacity) * 100) : 0;
  return (
    <li className="grid grid-cols-[6.5rem_1fr_auto] items-center gap-3 text-sm">
      <span className={muted ? 'text-muted-foreground' : undefined}>{label}</span>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            'h-full rounded-full',
            muted && 'bg-muted-foreground/30',
            !muted && hot && 'bg-destructive',
            !muted && !hot && 'bg-foreground',
          )}
          style={{ width: `${width}%` }}
        />
      </div>
      <span className="tabular-nums text-muted-foreground">
        {hot ? `${hours}h / ${capacity}h` : `${hours}h`}
      </span>
    </li>
  );
}

function hoursOf(items: ResourceWorkItem[]): number {
  return Math.round(items.reduce((sum, item) => sum + item.allocated_hours, 0) * 10) / 10;
}

function Count({ label, value }: { label: string; value: number }) {
  return (
    <div className="text-right">
      <dt className="text-xl font-semibold tabular-nums">{value}</dt>
      <dd className="text-xs text-muted-foreground">{label}</dd>
    </div>
  );
}

function CapacityTrack({ allocated, capacity }: { allocated: number; capacity: number }) {
  const max = Math.max(allocated, capacity, 1);
  const capPct = (capacity / max) * 100;
  const allocPct = Math.min(100, (allocated / max) * 100);
  const over = allocated > capacity;
  return (
    <div className="pt-1">
      <div className="relative h-2 overflow-hidden rounded-full bg-muted">
        <div
          className="absolute inset-y-0 left-0 bg-foreground/35"
          style={{ width: `${over ? capPct : allocPct}%` }}
        />
        {over ? (
          <div
            className="absolute inset-y-0 bg-destructive"
            style={{ left: `${capPct}%`, width: `${allocPct - capPct}%` }}
          />
        ) : null}
      </div>
      <div className="relative mt-1.5 h-4 text-[11px] tabular-nums text-muted-foreground">
        <span className="absolute left-0">0h</span>
        <span
          className="absolute -translate-x-1/2"
          style={{ left: `${capPct}%` }}
        >
          {capacity}h capacity
        </span>
        <span className="absolute right-0">{Math.max(allocated, capacity)}h</span>
      </div>
    </div>
  );
}

function WorkTable({ children }: { children: ReactNode }) {
  return (
    <Card className="rounded-xl border-border/80 shadow-none">
      <CardContent className="p-0">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-2.5 font-medium">Work item</th>
              <th className="w-28 px-3 py-2.5 font-medium">Priority</th>
              <th className="w-32 px-3 py-2.5 font-medium">State</th>
              <th className="w-16 px-4 py-2.5 text-right font-medium">Est.</th>
            </tr>
          </thead>
          {children}
        </table>
      </CardContent>
    </Card>
  );
}

function WorkSection({
  title,
  items,
  empty,
}: {
  title: string;
  items: ResourceWorkItem[];
  empty?: string;
}) {
  const [open, setOpen] = useState(true);
  return (
    <tbody>
      <tr className="border-b bg-muted/40">
        <td colSpan={4} className="px-4 py-2">
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="flex w-full items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground"
          >
            <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform', !open && '-rotate-90')} />
            {title}
            <span className="font-normal tabular-nums">· {items.length}</span>
            <span className="ml-auto tabular-nums">{hoursOf(items)}h</span>
          </button>
        </td>
      </tr>
      {open && items.length === 0 ? (
        <tr>
          <td colSpan={4} className="px-4 py-5 text-sm text-muted-foreground">
            {empty ?? 'None'}
          </td>
        </tr>
      ) : null}
      {open
        ? items.map((item) => (
            <tr key={item.id} className="border-b last:border-0">
              <td className="px-4 py-2.5 align-top">
                <div className="flex min-w-0 items-start gap-2">
                  <WorkItemKeyLink workItemId={item.id} workItemKey={item.key} projectId={item.project_id} />
                  <WorkItemTitleLink
                    workItemId={item.id}
                    workItemKey={item.key}
                    projectId={item.project_id}
                    title={item.title}
                  />
                </div>
              </td>
              <td className="px-3 py-2.5 align-top">
                <PriorityLabel priority={item.priority} />
              </td>
              <td className="px-3 py-2.5 align-top">
                <StateCell item={item} />
              </td>
              <td className="px-4 py-2.5 text-right align-top tabular-nums text-muted-foreground">
                {item.allocated_hours > 0 ? `${item.allocated_hours}h` : '—'}
              </td>
            </tr>
          ))
        : null}
    </tbody>
  );
}

function StateCell({ item }: { item: ResourceWorkItem }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span
        className={cn(
          'h-1.5 w-1.5 shrink-0 rounded-full',
          item.blocked && 'bg-destructive',
          !item.blocked && item.state_group === 'started' && 'bg-foreground/60',
          !item.blocked && item.state_group !== 'started' && 'bg-muted-foreground/50',
        )}
        aria-hidden
      />
      {item.state_name}
    </span>
  );
}

